import { invariant } from '@epic-web/invariant';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { buildUrlWithParams } from '@src/utils/rtkQuery/buildUrlWithParams';
import { httpClientThrownToRestEndpointErrorResponse } from '@src/utils/rtkQuery/restFailureError';
import { Delete, Get, Patch, Post, Put, type ErrorResponse, type RestResponse } from '@talos/kyoko';
import type { AppState } from './types';

/** Query parameters - use Record for arbitrary params or extend for specific endpoints */
export type RestQueryParams = Record<string, string | number | boolean | undefined>;

/** Common pagination parameters */
export type PaginationParams = {
  limit?: number;
  page?: string;
  next?: string;
} & RestQueryParams;

/** Base query arguments for restEndpointBaseQuery */
export interface RestEndpointQueryArgs {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  baseUrl?: string;
  body?: unknown;
  headers?: HeadersInit;
  /** Query parameters to append to the URL */
  params?: PaginationParams;
  /** POST/PUT/PATCH/DELETE requests normally send an empty object when body is omitted. Set this for endpoints with no body. */
  withoutBody?: boolean;
  /** Max records to fetch - enables automatic pagination via kyoko until no `next` or limit is reached */
  paginateRecords?: number;
}

/**
 * Base Query for RTK Query createApi for Ava REST requests (GET, POST, PUT, PATCH, DELETE)
 *
 * @example
 * ```ts
 * // Simple GET request
 * getItems: builder.query<Item[], void>({
 *   query: () => ({ method: 'GET', url: '/items' }),
 * }),
 *
 * // With query parameters
 * getItems: builder.query<Item[], PaginationParams>({
 *   query: (params) => ({ method: 'GET', url: '/items', params }),
 * }),
 *
 * // Automatic pagination - fetches all pages up to limit (uses kyoko's paginateRecords)
 * getAllItems: builder.query<Item[], void>({
 *   query: () => ({ method: 'GET', url: '/items', paginateRecords: 5000 }),
 * }),
 * ```
 */
export const restEndpointBaseQuery: BaseQueryFn<RestEndpointQueryArgs, unknown, ErrorResponse> = async (
  { url, method, baseUrl, body, headers, params, withoutBody, paginateRecords },
  api,
  _extraOptions
) => {
  const currentState = api.getState() as AppState;
  invariant(currentState.userInfo.user, "User should be known before any api's are called");
  const orgApiEndpoint = baseUrl ?? `//${currentState.userInfo.user.OrgApiEndpoint}/v1`;

  const finalUrl = buildUrlWithParams(url, params);
  const requestBody = body as object | null | undefined;

  const options = {
    ...(headers && { headers }),
    ...(withoutBody && { body: undefined }),
    ...(paginateRecords != null && { paginateRecords }),
  };

  const methodMap = {
    GET: () => Get<RestResponse<unknown>>(orgApiEndpoint, finalUrl, options),
    POST: () => Post<RestResponse<unknown>>(orgApiEndpoint, finalUrl, requestBody, options),
    PUT: () => Put<RestResponse<unknown>>(orgApiEndpoint, finalUrl, requestBody, options),
    PATCH: () => Patch<RestResponse<unknown>>(orgApiEndpoint, finalUrl, requestBody, options),
    DELETE: () => Delete<RestResponse<unknown>>(orgApiEndpoint, finalUrl, requestBody, options),
  } as const;

  try {
    const result = await methodMap[method]();

    if (result == null) {
      return { data: undefined };
    }

    return { data: typeof result === 'object' && 'data' in result ? result.data : result };
  } catch (error: unknown) {
    return { error: httpClientThrownToRestEndpointErrorResponse(error) };
  }
};
